import {
  AdminDatePickerInputModal,
  AdminRadioInput,
  Button,
  dateTimeInitial,
  Div,
  ErrorToast,
  H5,
  Modal,
  PropsModal,
  Separator,
  SuccessToast,
  updateMenuSchedule,
  UpdateMenuScheduleV2Dto,
  WeekDay,
  reportError,
} from '@vgrubs/components';
import {
  daysOfWeekStringV2,
  MenuAvailabilityHourV2Response,
  MenuAvailabilityV2Response,
  MenuV2Response,
} from 'components/src/types/menu.types';
import { Provider } from 'components/src/types/shared.types';
import { DateTime } from 'luxon';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import {
  format24hToDate,
  formatDateToTime24h,
} from '../../../../helpers/time-helpers';

export const AddEditSchedule = ({
  modalIsOpen,
  closeModal,
  setLoading,
  setMenu,
  item,
  edit,
}: PropsModal & {
  modalIsOpen: MenuAvailabilityV2Response | null;
  setMenu: Dispatch<SetStateAction<MenuV2Response | null>>;
  item?: MenuAvailabilityHourV2Response;
  edit?: boolean;
}) => {
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<WeekDay | null>(
    null
  );
  const [timeFrom, setTimeFrom] = useState<Date[]>([dateTimeInitial]);
  const [timeTo, setTimeTo] = useState<Date[]>([dateTimeInitial]);

  const { provider, locationId, brandId } = useParams() as {
    provider: Provider;
    locationId: string;
    brandId: string;
  };

  const data = modalIsOpen;

  useEffect(() => {
    if (edit && item) {
      setSelectedDayOfWeek(item.day);
      setTimeFrom(
        item.startTime ? [format24hToDate(item.startTime)] : [dateTimeInitial]
      );
      setTimeTo(
        item.endTime ? [format24hToDate(item.endTime)] : [dateTimeInitial]
      );
    } else {
      setSelectedDayOfWeek(null);
      setTimeFrom([dateTimeInitial]);
      setTimeTo([dateTimeInitial]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  async function handleAdd() {
    if (!data) return;
    if (selectedDayOfWeek === null || !timeFrom || !timeTo)
      return ErrorToast('Please fill in all information');

    // Parse the time values with Luxon
    const timeFromDate = DateTime.fromFormat(
      formatDateToTime24h(timeFrom[0]),
      'HH:mm'
    );
    const timeToDate = DateTime.fromFormat(
      formatDateToTime24h(timeTo[0]),
      'HH:mm'
    );

    // Ensure "Time From" is earlier than "Time To"
    if (timeFromDate >= timeToDate)
      return ErrorToast('Time From must be earlier than Time To');

    // Ensure at least 1 hour difference between "From" and "To"
    const timeDifference = timeToDate.diff(timeFromDate, 'hours').hours;
    if (timeDifference < 1)
      return ErrorToast(
        'There must be at least 1 hour between Time From and Time To'
      );

    const hrsEdit = [] as MenuAvailabilityHourV2Response[];

    if (edit && item) {
      data?.hours?.forEach((element) => {
        if (element.id === item.id) {
          hrsEdit.push({
            day: selectedDayOfWeek,
            startTime: formatDateToTime24h(timeFrom[0]),
            endTime: formatDateToTime24h(timeTo[0]),
          });
        } else
          return hrsEdit.push({
            day: element.day,
            startTime: element.startTime,
            endTime: element.endTime,
          });
      });
    }

    const hrsAdd = [] as MenuAvailabilityHourV2Response[];

    if (!edit) {
      data?.hours?.forEach((element) =>
        hrsAdd.push({
          day: element.day,
          startTime: element.startTime,
          endTime: element.endTime,
        })
      );

      hrsAdd.push({
        day: selectedDayOfWeek,
        startTime: formatDateToTime24h(timeFrom[0]),
        endTime: formatDateToTime24h(timeTo[0]),
      });
    }

    const bodyEdit: UpdateMenuScheduleV2Dto = {
      name: data.name,
      hours: hrsEdit,
    };

    const bodyAdd: UpdateMenuScheduleV2Dto = {
      name: data.name,
      hours: hrsAdd,
    };

    const body = edit ? bodyEdit : bodyAdd;

    try {
      setLoading?.(true);

      const { data } = await updateMenuSchedule(
        locationId,
        brandId,
        modalIsOpen.id,
        body
      );
      setMenu(data?.data);
      closeModal?.();
      SuccessToast(data.message);
      setSelectedDayOfWeek(null);
      setTimeFrom([dateTimeInitial]);
      setTimeTo([dateTimeInitial]);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading?.(false);
    }
  }

  return (
    <Modal
      isOpen={!!modalIsOpen}
      closeModal={closeModal}
      logo={false}
      header={`${edit ? 'Edit' : 'Add'} Schedule`}
      textAlign="left"
      width="500px"
    >
      <H5 $center>Select Day</H5>
      <Separator />
      {daysOfWeekStringV2.map(({ name, value }) => (
        <div key={value}>
          <AdminRadioInput
            label={name}
            checked={selectedDayOfWeek === value}
            onClick={() => setSelectedDayOfWeek(value)}
          />
          <Separator />
        </div>
      ))}
      <H5 $mt="32" $mb="16" $center>
        Select Hours From - To
      </H5>
      <Div $flex $gap="24px">
        <AdminDatePickerInputModal
          format="hh:mm a"
          options={{
            enableTime: true,
            dateFormat: 'Y-m-d H:i',
            noCalendar: true,
          }}
          placeholder="From"
          $textCenter
          onChange={setTimeFrom}
          value={timeFrom}
        />
        <AdminDatePickerInputModal
          format="hh:mm a"
          options={{
            enableTime: true,
            dateFormat: 'Y-m-d H:i',
            noCalendar: true,
          }}
          placeholder="To"
          $textCenter
          onChange={setTimeTo}
          value={timeTo}
        />
      </Div>

      <Button $selected $w="100%" $mt="32" onClick={() => handleAdd()}>
        Confirm
      </Button>
      <Button $selected $gray $w="100%" $mt="16" onClick={() => closeModal?.()}>
        Cancel
      </Button>
    </Modal>
  );
};
